import { useState } from 'react';
import { useQueryParams } from '../../../../../hooks/useQueryParams';
import useTimeout from '../../../../../hooks/useTimeout';
import { InjectAppServices } from '../../../../../services/pure-di';
import { ACCOUNT_TYPE } from '../../../../../hooks/useUserTypeAsQueryParam';
import { getCheckoutErrorMesage } from '../../utils';
import { FREE_ACCOUNT, PAID_ACCOUNT } from '../../../../../utils';
import { useIntl } from 'react-intl';
import {
  AddOnType,
  BUY_CHAT_PLAN,
  BUY_ONSITE_PLAN,
  BUY_PUSH_NOTIFICATION_PLAN,
} from '../../../../../doppler-types';
import { MobileCheckoutAction } from '../../MobileCheckoutAction';

export const DELAY_BEFORE_REDIRECT_TO_SUMMARY = 3000;
const HAS_ERROR = 'HAS_ERROR';
const SAVING = 'SAVING';
const SAVED = 'SAVED';

export const AddOnCheckoutButton = InjectAppServices(
  ({
    dependencies: { dopplerBillingUserApiClient, appSessionRef },
    keyTextButton,
    canBuy = false,
    total,
    discount,
    addOnPlanId,
    buyType,
    selectedAddOnPlan,
    paymentMethod,
  }) => {
    const intl = useIntl();
    const _ = (id, values) => intl.formatMessage({ id: id }, values);
    const [status, setStatus] = useState('');
    const [messageError, setMessageError] = useState('');
    const createTimeout = useTimeout();
    const query = useQueryParams();
    const { isFreeAccount } = appSessionRef.current.userData.user.plan;
    const accountType = query.get(ACCOUNT_TYPE) ?? (isFreeAccount ? FREE_ACCOUNT : PAID_ACCOUNT);

    const getCheckoutSummaryUrl = () => {
      const queryParams = [`buyType=${buyType}`];

      if (paymentMethod) {
        queryParams.push(`paymentMethod=${paymentMethod}`);
      }

      queryParams.push(`${ACCOUNT_TYPE}=${accountType}`);

      if (discount?.subscriptionType) {
        queryParams.push(`discount=${discount.subscriptionType}`);
      }

      if (addOnPlanId) {
        queryParams.push(`addOnPlanId=${addOnPlanId}`);
      }

      return `/checkout-summary?${queryParams.join('&')}`;
    };

    const proceedToBuy = async () => {
      setStatus(SAVING);
      const response = await dopplerBillingUserApiClient.purchaseAddOnPlan({
        total,
        addOnType:
          buyType === BUY_ONSITE_PLAN
            ? AddOnType.OnSite
            : buyType === BUY_PUSH_NOTIFICATION_PLAN
              ? AddOnType.PushNotifications
              : buyType === BUY_CHAT_PLAN
                ? AddOnType.Conversations
                : AddOnType.EcoAI,
        addOnPlanId,
      });

      if (response.success) {
        setStatus(SAVED);
        sessionStorage.setItem('amount', selectedAddOnPlan?.total ?? total);
        createTimeout(() => {
          window.location.href = getCheckoutSummaryUrl();
        }, DELAY_BEFORE_REDIRECT_TO_SUMMARY);
      } else {
        setMessageError(getCheckoutErrorMesage(response.error.response?.data));
        setStatus(HAS_ERROR);
      }
    };

    const cancelAddOnPlan = async () => {
      setStatus(SAVING);
      const addOnType =
        buyType === BUY_ONSITE_PLAN
          ? AddOnType.OnSite
          : buyType === BUY_PUSH_NOTIFICATION_PLAN
            ? AddOnType.PushNotifications
            : buyType === BUY_CHAT_PLAN
              ? AddOnType.Conversations
              : AddOnType.EcoAI;

      const response = await dopplerBillingUserApiClient.cancellationAddOnPlan(addOnType);
      if (response.success) {
        setStatus(SAVED);
        createTimeout(() => {
          window.location.href = getCheckoutSummaryUrl();
        }, DELAY_BEFORE_REDIRECT_TO_SUMMARY);
      } else {
        setMessageError(getCheckoutErrorMesage(response.error.response?.data));
        setStatus(HAS_ERROR);
      }
    };

    const disabledBuy = !canBuy || [SAVING, SAVED].includes(status);
    const showMessage = [SAVED, HAS_ERROR].includes(status);

    return (
      <>
        <MobileCheckoutAction>
          <button
            type="button"
            className={
              'dp-button button-big primary-green' + (status === SAVING ? ' button--loading' : '')
            }
            disabled={disabledBuy}
            onClick={parseInt(addOnPlanId) === 0 ? cancelAddOnPlan : proceedToBuy}
            aria-label="buy"
          >
            {_(keyTextButton)}
          </button>
        </MobileCheckoutAction>
        <button type="button" className="dp-button button-big dp-secure-payment">
          <span className="ms-icon dpicon iconapp-padlock"></span>
          {_('checkoutProcessForm.purchase_summary.secure_payment_message')}
        </button>
        {showMessage && (
          <StatusMessage
            type={status === SAVED ? 'success' : 'cancel'}
            message={_(
              status === SAVED
                ? 'checkoutProcessForm.purchase_summary.success_message'
                : messageError,
            )}
          />
        )}
      </>
    );
  },
);

export const StatusMessage = ({ type, message }) => (
  <div className={`dp-wrap-message dp-wrap-${type}`} role="alert" aria-label={type}>
    <span className="dp-message-icon" />
    <div className="dp-content-message">
      <p>{message}</p>
    </div>
  </div>
);
